import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core'; // Force recompile
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { GifPanelComponent } from '../gif-panel/gif-panel.component';
import { ChatSortPipe } from '../../../../pipe/chat-sort.pipe';
import {
  isImage, isVideo, isAudio, isDocument, isURL, DOCUMENT_EXTENSIONS,
  getReplyThumbnail, getReplyMediaType, getReplyMessageText,
  getReplyMediaTypeIcon, isMessageRedundantWithFile
} from '../../utils/helpers.utils';


@Component({
  selector: 'app-message-input',
  standalone: true,
  imports: [CommonModule, FormsModule, PickerModule, GifPanelComponent, ChatSortPipe],
  templateUrl: './message-input.component.html',
  styleUrls: ['./message-input.component.css']
})
export class MessageInputComponent implements AfterViewInit, OnChanges {
  @Input() message: string = '';
  @Input() placeholder: string = 'Type a message';
  private _replyingTo: any = null;
  replyThumbnailError: boolean = false;

  @Input()
  set replyingTo(val: any) {
    this._replyingTo = val;
    this.replyThumbnailError = false;
  }
  get replyingTo(): any {
    return this._replyingTo;
  }
  @Input() editingMessage: any = null;
  @Input() showEmojiPicker: boolean = false;
  @Input() showGifPanel: boolean = false;
  @Input() mentionUsers: any[] = [];
  @Input() mentionSearchTerm: string = '';
  @Input() showMentionList: boolean = false;
  @Input() isGroup: boolean = false;
  @Input() selectedGifUrl: string = '';
  @Input() isExited: boolean = false;
  @Input() userId: string = '';
  @Input() isTyping: boolean = false;
  @Input() isSending: boolean = false;
  @Input() isMobileView: boolean = false;
  showAttachMenu: boolean = false;

  get isMobile(): boolean {
    if (this.isMobileView) return true;
    if (typeof window !== 'undefined') {
      const hasTouch = 'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
      const isNarrow = window.innerWidth <= 768;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
      return (hasTouch && isNarrow) || isMobileUA || isNarrow;
    }
    return false;
  }
  
  isRecordingVoice: boolean = false;
  voiceRecordingTime: number = 0;
  private mediaRecorder: MediaRecorder | null = null;
  private voiceChunks: Blob[] = [];
  private voiceTimer: any = null;
  private mediaStream: MediaStream | null = null;

  @Output() messageSent = new EventEmitter<Event>();
  @Output() fileAttached = new EventEmitter<Event>();
  @Output() gifSelected = new EventEmitter<any>();
  @Output() replyCancelled = new EventEmitter<void>();
  @Output() editCancelled = new EventEmitter<void>();
  @Output() inputChanged = new EventEmitter<Event>();
  @Output() pasteAction = new EventEmitter<ClipboardEvent>();
  @Output() emojiToggle = new EventEmitter<void>();
  @Output() gifToggle = new EventEmitter<void>();
  @Output() cameraOpened = new EventEmitter<void>();
  @Output() mentionSelected = new EventEmitter<any>();
  @Output() messageChange = new EventEmitter<string>();
  @Output() typingStart = new EventEmitter<void>();
  @Output() typingStop = new EventEmitter<void>();
  @Output() editLastMessage = new EventEmitter<void>();

  @ViewChild('messageInput') messageInputRef!: ElementRef;
  private typingTimer: any = null;
  showFormattingBar: boolean = false;

  ngAfterViewInit() {
    this.adjustTextareaHeight();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['message'] || changes['editingMessage']) {
      setTimeout(() => this.adjustTextareaHeight(), 0);
    }
  }

  public adjustTextareaHeight(): void {
    const textarea = this.messageInputRef?.nativeElement;
    if (!textarea) return;

    // Reset height to auto first so scrollHeight accurately shrinks on text removal
    textarea.style.height = 'auto';

    if (!textarea.value && !this.message) {
      textarea.style.height = '';
      textarea.style.overflowY = 'hidden';
      return;
    }

    const maxHeight = 160; // Max height before vertical scrollbar appears (approx 6-7 lines)
    const scrollHeight = textarea.scrollHeight;

    if (scrollHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    } else if (scrollHeight > 0) {
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
  }

  mentionActiveIndex: number = 0;

  onInput(event: Event) {
    this.mentionActiveIndex = 0;
    this.adjustTextareaHeight();
    this.inputChanged.emit(event);
    
    // Emit typing start event
    this.typingStart.emit();
    
    // Clear existing timer and set a new one
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
    
    // Emit typing stop after 2 seconds of inactivity
    this.typingTimer = setTimeout(() => {
      this.typingStop.emit();
    }, 2000);
  }

  onKeyDown(event: any) {
    if (this.isGroup && this.showMentionList && this.mentionUsers && this.mentionUsers.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.mentionActiveIndex = (this.mentionActiveIndex + 1) % this.mentionUsers.length;
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.mentionActiveIndex = (this.mentionActiveIndex - 1 + this.mentionUsers.length) % this.mentionUsers.length;
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const selected = this.mentionUsers[this.mentionActiveIndex] || this.mentionUsers[0];
        if (selected) {
          this.selectMention(selected);
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.showMentionList = false;
        return;
      }
    }

    if (event.key === 'ArrowUp' && (!this.message || this.message.trim().length === 0)) {
      event.preventDefault();
      this.editLastMessage.emit();
      return;
    }

    if (event.key === 'Escape') {
      if (this.isEditMode) {
        event.preventDefault();
        this.cancelEdit();
        return;
      }
      if (this.replyingTo) {
        event.preventDefault();
        this.cancelReply();
        return;
      }
    }

    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const modKey = isMac ? event.metaKey : event.ctrlKey;

    // Telegram formatting shortcuts:
    // Cmd/Ctrl+Shift+P: Hidden text (spoiler)
    if (modKey && event.shiftKey && (event.key === 'p' || event.key === 'P')) {
      event.preventDefault();
      this.applyTextFormatting('spoiler');
      return;
    }
    // Cmd/Ctrl+B: Bold
    if (modKey && !event.shiftKey && (event.key === 'b' || event.key === 'B')) {
      event.preventDefault();
      this.applyTextFormatting('bold');
      return;
    }
    // Cmd/Ctrl+I: Italic
    if (modKey && !event.shiftKey && (event.key === 'i' || event.key === 'I')) {
      event.preventDefault();
      this.applyTextFormatting('italic');
      return;
    }
    // Cmd/Ctrl+Shift+X: Strikethrough
    if (modKey && event.shiftKey && (event.key === 'x' || event.key === 'X')) {
      event.preventDefault();
      this.applyTextFormatting('strikethrough');
      return;
    }
    // Cmd/Ctrl+Shift+M: Monospace / Code
    if (modKey && event.shiftKey && (event.key === 'm' || event.key === 'M')) {
      event.preventDefault();
      this.applyTextFormatting('code');
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      if (this.isMobile) {
        // On mobile, pressing Enter/Return inserts a new line (shifts message to next line)
        // Let default textarea behavior insert the newline, then adjust height
        setTimeout(() => this.adjustTextareaHeight(), 0);
        return;
      }
      event.preventDefault();
      this.showFormattingBar = false;
      this.messageSent.emit(event);
      setTimeout(() => {
        this.adjustTextareaHeight();
        this.focus();
      }, 50);
    } else if (event.key === 'Enter' && event.shiftKey) {
      setTimeout(() => this.adjustTextareaHeight(), 0);
    }
  }

  onTextareaSelection(event?: any) {
    const textarea = this.messageInputRef?.nativeElement;
    if (!textarea) {
      this.showFormattingBar = false;
      return;
    }
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    if (end > start && textarea.value.substring(start, end).trim().length > 0) {
      this.showFormattingBar = true;
    } else {
      this.showFormattingBar = false;
    }
  }

  onTextareaBlur(event?: FocusEvent) {
    setTimeout(() => {
      const activeEl = document.activeElement;
      if (!activeEl || !activeEl.closest('.formatting-toolbar-floating')) {
        this.showFormattingBar = false;
      }
    }, 200);
  }

  applyTextFormatting(format: 'spoiler' | 'bold' | 'italic' | 'strikethrough' | 'code' | 'quote') {
    const textarea = this.messageInputRef?.nativeElement;
    if (!textarea) return;

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const currentVal = textarea.value || '';
    const selectedText = currentVal.substring(start, end);

    let prefix = '';
    let suffix = '';
    let placeholder = '';

    switch (format) {
      case 'spoiler':
        prefix = '||';
        suffix = '||';
        placeholder = 'hidden text';
        break;
      case 'bold':
        prefix = '**';
        suffix = '**';
        placeholder = 'bold text';
        break;
      case 'italic':
        prefix = '_';
        suffix = '_';
        placeholder = 'italic text';
        break;
      case 'strikethrough':
        prefix = '~';
        suffix = '~';
        placeholder = 'strikethrough text';
        break;
      case 'code':
        prefix = '`';
        suffix = '`';
        placeholder = 'code';
        break;
      case 'quote':
        prefix = '> ';
        suffix = '';
        placeholder = 'quote';
        break;
    }

    // Toggle off if already wrapped
    if (selectedText && selectedText.startsWith(prefix) && suffix && selectedText.endsWith(suffix) && selectedText.length >= prefix.length + suffix.length) {
      const unwrapped = selectedText.substring(prefix.length, selectedText.length - suffix.length);
      const newText = currentVal.substring(0, start) + unwrapped + currentVal.substring(end);
      this.onModelChange(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start + unwrapped.length);
        this.adjustTextareaHeight();
        this.onTextareaSelection();
      }, 0);
      return;
    }

    const textToWrap = selectedText || placeholder;
    const newText = currentVal.substring(0, start) + prefix + textToWrap + suffix + currentVal.substring(end);

    this.onModelChange(newText);

    setTimeout(() => {
      textarea.focus();
      if (selectedText) {
        textarea.setSelectionRange(start + prefix.length, start + prefix.length + selectedText.length);
      } else {
        textarea.setSelectionRange(start + prefix.length, start + prefix.length + placeholder.length);
      }
      this.adjustTextareaHeight();
      this.onTextareaSelection();
    }, 0);
  }

  onPaste(event: ClipboardEvent) {
    this.pasteAction.emit(event);
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  toggleEmoji() {
    this.emojiToggle.emit();
  }

  addEmoji(event: any) {
    const emoji = event.emoji.native;
    this.message = (this.message || '') + emoji;
    this.messageChange.emit(this.message);
    setTimeout(() => {
      this.adjustTextareaHeight();
      this.focus();
    }, 0);
  }

  toggleGif() {
    this.showAttachMenu = false;
    this.gifToggle.emit();
  }

  cancelReply() {
    this.replyCancelled.emit();
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  cancelEdit() {
    this.editCancelled.emit();
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  get isEditMode(): boolean {
    return !!this.editingMessage;
  }

  get editModePlaceholder(): string {
    return this.isEditMode ? 'Edit message...' : this.placeholder;
  }

  handleFileInput(event: Event) {
    this.fileAttached.emit(event);
  }

  openCamera() {
    this.showAttachMenu = false;
    this.cameraOpened.emit();
  }

  toggleAttachMenu() {
    this.showAttachMenu = !this.showAttachMenu;
  }

  triggerPhotosVideoInput(fileInput: HTMLInputElement) {
    this.showAttachMenu = false;
    fileInput.accept = 'image/*,video/*';
    fileInput.click();
  }

  triggerDocumentInput(fileInput: HTMLInputElement) {
    this.showAttachMenu = false;
    fileInput.accept = DOCUMENT_EXTENSIONS.join(',');
    fileInput.click();
  }

  triggerAudioInput(fileInput: HTMLInputElement) {
    this.showAttachMenu = false;
    fileInput.accept = 'audio/*';
    fileInput.click();
  }

  async startVoiceRecording() {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.voiceChunks = [];
      this.mediaRecorder = new MediaRecorder(this.mediaStream);
      
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.voiceChunks.push(e.data);
      };

      this.mediaRecorder.onstop = () => {
        if (this.voiceChunks.length > 0 && this.isRecordingVoice) {
          if (this.voiceRecordingTime > 0) {
            const blob = new Blob(this.voiceChunks, { type: 'audio/mpeg' });
            const file: any = new File([blob], `voice_message_${Date.now()}.mp3`, { type: 'audio/mpeg' });
            file.duration = this.voiceRecordingTime;
            this.fileAttached.emit({ target: { files: [file] } } as any);
          }
        }
        this.resetVoiceRecording();
      };

      this.mediaRecorder.start();
      this.isRecordingVoice = true;
      this.voiceRecordingTime = 0;
      this.voiceTimer = setInterval(() => {
        this.voiceRecordingTime++;
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone', err);
    }
  }

  cancelVoiceRecording() {
    if (this.mediaRecorder && this.isRecordingVoice) {
      this.isRecordingVoice = false;
      this.mediaRecorder.stop();
    }
    this.resetVoiceRecording();
  }

  stopAndSendVoiceRecording() {
    if (this.mediaRecorder && this.isRecordingVoice) {
      this.mediaRecorder.stop();
    }
  }

  resetVoiceRecording() {
    this.isRecordingVoice = false;
    this.voiceChunks = [];
    if (this.voiceTimer) clearInterval(this.voiceTimer);
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    this.mediaRecorder = null;
  }

  getFormattedVoiceTime(): string {
    const mins = Math.floor(this.voiceRecordingTime / 60);
    const secs = this.voiceRecordingTime % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  onModelChange(value: string) {
    this.message = value;
    this.messageChange.emit(value);
    this.adjustTextareaHeight();
  }

  selectMention(user: any) {
    this.showAttachMenu = false;
    this.mentionSelected.emit(user);
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  selectGif(gif: any) {
    this.gifSelected.emit(gif);
  }

  handleProfileImageError(event: any) {
    event.target.src = 'assets/user-icon.png';
  }

  getReplySenderName(replyingTo: any): string {
    if (!replyingTo) return 'User';
    const senderId = replyingTo.sender_id || replyingTo.senderId || replyingTo.userId || replyingTo.sid || replyingTo.meta?.sender_id;
    if (this.userId && senderId && String(senderId).trim() === String(this.userId).trim()) {
      return 'You';
    }
    const rawName = (replyingTo.senderName || replyingTo.name || '').trim();
    if (rawName.toLowerCase() === 'you') return 'You';

    const selfNames: string[] = [
      typeof localStorage !== 'undefined' ? localStorage.getItem('displayName') : null,
      typeof localStorage !== 'undefined' ? localStorage.getItem('username') : null,
      typeof localStorage !== 'undefined' ? localStorage.getItem('userName') : null,
      typeof localStorage !== 'undefined' ? localStorage.getItem('name') : null
    ].filter((n): n is string => !!n && typeof n === 'string').map(n => n.trim().toLowerCase());

    if (rawName && selfNames.some(own => own === rawName.toLowerCase())) {
      return 'You';
    }
    return replyingTo.senderName || replyingTo.name || 'User';
  }

  getReplyThumbnail(msg: any): string {
    return getReplyThumbnail(msg) || '';
  }

  isImage(url: string | any): boolean {
    return isImage(url);
  }

  isVideo(url: string | any): boolean {
    return isVideo(url);
  }

  isAudio(url: string | any): boolean {
    return isAudio(url);
  }

  isDocument(url: string | any): boolean {
    return isDocument(url);
  }


  getReplyMediaTypeIcon(msg: any): string {
    return getReplyMediaTypeIcon(msg);
  }

  getReplyMediaType(msg: any): string {
    return getReplyMediaType(msg);
  }


  isMessageRedundantWithFile(message: any): boolean {
    return isMessageRedundantWithFile(message);
  }


  getReplyMessageText(msg: any): string {
    return getReplyMessageText(msg);
  }

  private isURL(str: string): boolean {
    return isURL(str);
  }

  public focus() {
    if (this.messageInputRef) {
      this.messageInputRef.nativeElement.focus();
    }
  }

  onReplyThumbnailError() {
    this.replyThumbnailError = true;
  }

}
